import { Injectable } from "@angular/core";
import { Tutorial } from "@core/services/tutorial.service";
import { TranslateService } from "@ngx-translate/core";
import { TUTORIAL_TEMPLATES } from "@shared/constants/tutorial.constants";

@Injectable({
  providedIn: "root"
})
export class TutorialFactoryService {
  constructor(private readonly translateService: TranslateService) {}

  /**
   * Creates a fully translated tutorial instance from a template
   */
  public createTutorial(templateKey: keyof typeof TUTORIAL_TEMPLATES): Tutorial {
    const template = TUTORIAL_TEMPLATES[templateKey];

    if (!template) {
      throw new Error(`Tutorial template with key "${templateKey}" not found`);
    }

    // Create a copy of the tutorial
    const tutorial = {
      ...template,
      steps: [...template.steps]
    };

    // Translate content based on step ID using literals to ensure proper extraction
    tutorial.steps = template.steps.map(step => {
      const translatedStep = { ...step };

      if (templateKey === "IMAGE_TOOLS") {
        if (step.id === "menu-button") {
          translatedStep.content = this.translateService.instant(
            "Access image tools like histogram, sky map, measurements, and other features."
          );
        }
      } else if (templateKey === "MEASUREMENT_TOOL_TOOLBAR") {
        if (step.id === "toolbar-help") {
          translatedStep.content = this.translateService.instant(
            "Click here to view comprehensive help for the measurement tool features."
          );
        } else if (step.id === "toolbar-saved") {
          translatedStep.content = this.translateService.instant(
            "Access your saved measurements that can be loaded on any image with plate-solving data."
          );
        } else if (step.id === "toolbar-share") {
          translatedStep.content = this.translateService.instant(
            "Generate a URL that includes your measurements to share with others."
          );
        } else if (step.id === "toolbar-clear") {
          translatedStep.content = this.translateService.instant("Remove all measurements from the current image.");
        } else if (step.id === "draw-line") {
          translatedStep.content = this.translateService.instant(
            "Click and drag to draw a measurement line. The distance will be shown in arcminutes and arcseconds."
          );
        }
      } else if (templateKey === "MEASUREMENT_TOOL_EDITING") {
        if (step.id === "measurement-distance") {
          translatedStep.content = this.translateService.instant(
            "This box shows the angular distance. You can click and drag it to reposition the entire measurement."
          );
        } else if (step.id === "toggle-shape-circle") {
          translatedStep.content = this.translateService.instant(
            "Toggle circle visualization to show the measured distance as a circle."
          );
        } else if (step.id === "toggle-shape-rectangle") {
          translatedStep.content = this.translateService.instant(
            "Toggle rectangle visualization to show width and height dimensions."
          );
        } else if (step.id === "export-coordinates") {
          translatedStep.content = this.translateService.instant(
            "Export the measurement coordinates for use in other applications."
          );
        } else if (step.id === "save-measurements") {
          translatedStep.content = this.translateService.instant(
            "Save this measurement to your account for future reference."
          );
        }
      } else if (templateKey === "ANNOTATION_TOOL_TOOLBAR") {
        if (step.id === "annotation-help") {
          translatedStep.content = this.translateService.instant(
            "Click here to view comprehensive help for the annotation tool features."
          );
        } else if (step.id === "annotation-rectangle") {
          translatedStep.content = this.translateService.instant(
            "Add a rectangular annotation to highlight features in this image."
          );
        } else if (step.id === "annotation-circle") {
          translatedStep.content = this.translateService.instant(
            "Add a circular annotation to highlight features in this image."
          );
        } else if (step.id === "annotation-text") {
          translatedStep.content = this.translateService.instant(
            "Add a text annotation to label features in this image."
          );
        } else if (step.id === "annotation-link") {
          translatedStep.content = this.translateService.instant(
            "Add a clickable link annotation to reference external resources."
          );
        } else if (step.id === "annotation-save") {
          translatedStep.content = this.translateService.instant(
            "Save annotations permanently to your image (only available to the image owner)."
          );
        } else if (step.id === "annotation-share") {
          translatedStep.content = this.translateService.instant(
            "Generate a URL that includes your annotations to share with others."
          );
        } else if (step.id === "annotation-clear") {
          translatedStep.content = this.translateService.instant("Remove all annotations from the current image.");
        } else if (step.id === "annotation-exit") {
          translatedStep.content = this.translateService.instant(
            "Exit annotation mode and return to viewing the image."
          );
        }
      } else if (templateKey === "FORUM_HOME") {
        if (step.id === "feed-dropdown") {
          translatedStep.content = this.translateService.instant(
            "Welcome to the new AstroBin forums! Switch between different feed types to see a personalized feed that you can customize, or all topics."
          );
        } else if (step.id === "tags-button") {
          translatedStep.content = this.translateService.instant(
            "Explore all available tags to find discussions about specific topics that interest you."
          );
        } else if (step.id === "new-topic-button") {
          translatedStep.content = this.translateService.instant(
            "Create a new discussion topic to ask questions or share your knowledge with the community."
          );
        } else if (step.id === "search-bar") {
          translatedStep.content = this.translateService.instant(
            "Search for topics using the improved search functionality."
          );
        } else if (step.id === "topic-tags") {
          translatedStep.content = this.translateService.instant(
            "Topics are categorized with tags to help you quickly identify relevant discussions."
          );
        } else if (step.id === "post-quality-panel") {
          translatedStep.content = this.translateService.instant(
            "Automatic quality indicators help identify answers by qualitative metrics."
          );
        } else if (step.id === "recommended-tags") {
          translatedStep.content = this.translateService.instant(
            "Based on your interests and activity, we suggest these tags that might be relevant to you."
          );
        } else if (step.id === "random-image") {
          translatedStep.content = this.translateService.instant(
            "You're all set to explore the new forums! The community thrives on your participation. Enjoy this complimentary random astrophotograph 😊"
          );
        }
      } else if (templateKey === "FORUM_TOPIC_TAGS") {
        if (step.id === "topic-tags") {
          translatedStep.content = this.translateService.instant(
            "Topic tags help categorize the discussion. Click on any tag to find related topics."
          );
        } else if (step.id === "edit-tags") {
          translatedStep.content = this.translateService.instant(
            "Topic authors and trusted forum contributors can edit tags to help better categorize discussions."
          );
        }
      } else if (templateKey === "FORUM_TOP_CONTRIBUTORS") {
        if (step.id === "top-contributors") {
          translatedStep.content = this.translateService.instant(
            "These users have made significant contributions to this discussion."
          );
        }
      } else if (templateKey === "FORUM_TABLE_OF_CONTENTS") {
        if (step.id === "table-of-contents") {
          translatedStep.content = this.translateService.instant(
            "Navigate through long discussions easily with this table of contents, auto-generated in-house."
          );
        }
      } else if (templateKey === "FORUM_COMMUNITY_NOTES") {
        if (step.id === "community-notes") {
          translatedStep.content = this.translateService.instant(
            "Community members can add helpful notes to provide additional context or corrections."
          );
        }
      } else if (templateKey === "FORUM_POST_BUTTONS") {
        if (step.id === "add-community-note") {
          translatedStep.content = this.translateService.instant(
            "Add a community note to provide helpful context or corrections to this post."
          );
        } else if (step.id === "recommend-expert") {
          translatedStep.content = this.translateService.instant(
            "Recommend this user as an expert in the relevant tags if their posts demonstrate expertise."
          );
        }
      } else if (templateKey === "UPLOADER_COPY_DATA") {
        if (step.id === "copy-data-dropdown") {
          translatedStep.content = this.translateService.instant(
            "Copy all data from one of your existing images to save time when uploading similar images."
          );
        }
      } else if (templateKey === "MESSAGES_REFRESH") {
        if (step.id === "messages-refresh-announcement") {
          translatedStep.content = this.translateService.instant(
            "We've updated the Messages pages to match the new AstroBin design — faster, cleaner, and easier to use."
          );
        }
      }

      return translatedStep;
    });

    return tutorial;
  }
}
